<script setup lang="ts">
import { computed, watch, onBeforeMount, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useToasterStore, useModalStore } from "@comp-lib/stores";
import { useUserStore } from "@/stores/userStore";
import TenantPickerView from "@/views/TenantPickerView.vue";
import MainLayout from "@/layouts/MainLayout.vue";
import CustomModal from "@comp-lib/components/CustomModal.vue";
import ToasterNotification from "@comp-lib/components/ToasterNotification.vue";
import LoadingScreen from "@/components/LoadingScreen.vue";
import RouteAccessWrapper from "@/components/RouteAccessWrapper.vue";
import { currentDefaultVersion } from "@/assets/mobiusVersioningData";
import type { CwaccWindowMessage } from "@/types";

const router = useRouter();
const route = useRoute();

const modalStore = useModalStore();
const { isModalOpen, modalData } = storeToRefs(modalStore);
const { openModal, closeModal, confirmModal } = modalStore;

const toasterStore = useToasterStore();
const { notifications } = storeToRefs(toasterStore);
const { removeNotif } = toasterStore;

const userStore = useUserStore();
const { authUser, appState, needsTenantSelection, storeState, isNavigating, loadingOverride } = storeToRefs(userStore);
const { initUserData, initAppState, logUserOut } = userStore;

const showLoadingScreen = computed(() =>
  !authUser.value || isNavigating.value || storeState.value === "switching"
);

const showTenantPicker = computed(() =>
  needsTenantSelection.value && !route.path.startsWith("/admin")
);

function handleMessage(e: MessageEvent) {
  const appVersion = currentDefaultVersion;
  const msgData: string = e.data ? e.data : "";

  if (!!msgData) {
    let msg: CwaccWindowMessage;
    const urlBase = `/api/cwacc/${appVersion}`;
    const noRedirectUrls: (string | RegExp)[] = [
      `${urlBase}/user/accounting_configuration`,
      new RegExp(`^${urlBase}/admin/companies/[^/]+/tasks$`),
    ];

    try {
      // some browser extensions may POST non JSON data to vue
      msg = JSON.parse(msgData);
    } catch { return }
    
    if (msg.url) {
      if (noRedirectUrls.includes(msg.url)) {
        return;
      } else if (msg.externalLink === true) {
        const popup = window.open(msg.url, "_blank");
        if (popup === null) {
          const domain = (new URL(msg.url));
          const modalTxt = `We detected your browser is blocking popups. Please click below to continue to ${domain.hostname}.`;
          openModal({
            title: "Notice",
            textContent: modalTxt,
            confirmMethod: () => window.location.href = msg.url,
          });
        }
      } else window.location.href = msg.url;
    }
    if (msg.pathname) {
      // Prevent redirect for routes which shouldn't be redirected to the catchall view
      const dontRedirectRoute = noRedirectUrls.some(route => {
        if (route instanceof RegExp) {
          return route.test(msg.pathname);
        }
        return msg.pathname === route || msg.pathname.startsWith(`${route}/`);
      });
      if (dontRedirectRoute) return;

      const msgPathname = msg.pathname.replace("/api/cwacc", "");
      appState.value.showIframe = true;
      if (msgPathname !== window.location.pathname) {
        router.push(msgPathname.replace(`/${appVersion}`, ""));
      }
    }
    if (msg.logout) logUserOut(
      `${process.env.APP_BASE_URL}/${appVersion}/user/`
    );
    if (msg.fetchState) initAppState();
    if (msg.showIframe !== undefined) appState.value.showIframe = msg.showIframe;
    if (msg.title) document.title = msg.title;
    if (msg.blockUI) appState.value.showIframe = false;
    if (msg.unblockUI) appState.value.showIframe = true;
  }
};

onBeforeMount(() => initUserData());

onMounted(() => {
  window.addEventListener("message", handleMessage);
});

watch(isModalOpen, () => !isModalOpen.value && closeModal());
</script>

<template>
  <div v-if="showLoadingScreen" class="loading-screen">
    <LoadingScreen :message="loadingOverride?.message" :longLoad="loadingOverride?.longLoad" />
  </div>
  <div v-else :class="['app-container', { 'modal-open': isModalOpen }]">
    <TenantPickerView v-if="showTenantPicker" />
    <template v-else>
      <MainLayout>
        <RouteAccessWrapper>
          <RouterView />
        </RouteAccessWrapper>
        <ToasterNotification
          :notifications="notifications"
          absolutePosition
          @@close="removeNotif($event)"
        />
      </MainLayout>
      <CustomModal
        :isModalOpen="isModalOpen"
        :modalData="modalData"
        @@close="closeModal"
        @@confirm="confirmModal"
      />
    </template>
  </div>
</template>

<style lang="scss">
body {
  margin: 0;
  background-color: map.get(v.$gray, "100");
  color: map.get(v.$gray, "900");
  .page-title {
    font-size: 2.5rem;
    font-weight: 600;
    margin-bottom: 30px;
    @media (max-width: map.get(v.$breakpoints, "sm")) {
      font-size: 2rem;
    }
  }
  a, .link {
    text-decoration: none;
    font-weight: map.get(v.$font-weight, "medium");
    color: map.get(v.$primary, "600");
    transition: .2s;
    cursor: pointer;
    display: inline-flex;
    gap: 10px;
    align-items: center;
    &:hover {
      color: map.get(v.$primary, "400");
    }
  }
  ul { list-style: none; }
  .app-container {
    height: 100dvh;
    display: flex;
    flex-direction: column;
    color: map.get(v.$gray, "800");
    scrollbar-gutter: stable;
  }
  .wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 50px 15px;
    flex-grow: 1;
    @media (max-width: map.get(v.$breakpoints, "md")) {
      padding-top: 30px;
    }
    & .leading-logo {
      height: 40px;
      margin-bottom: 30px;
      @media (max-width: map.get(v.$breakpoints, "md")) {
        height: 30px;
      }
    }
  }
  .data-table {
    $borderStyle: 1px solid map.get(v.$gray, "300");

    border: $borderStyle;
    border-radius: map.get(v.$border-radius, "sm");
    background-color: white;
    overflow: hidden;
    .table-row {
      display: flex;
      border-bottom: $borderStyle;
      font-size: 0.875rem;
      &:last-child {
        border-bottom: none;
      }
      .table-label, .table-value {
        flex-wrap: wrap;
        gap: 7px;
        padding: 7px 20px;
        color: map.get(v.$gray, "700");
        min-height: 38px;
      }
      .table-label {
        font-weight: map.get(v.$font-weight, "medium");
        background-color: map.get(v.$gray, "50");
        border-right: $borderStyle;
        display: flex;
        align-items: center;
        text-align: right;
        justify-content: flex-end;
        flex: 0 1 160px;
        padding: 7px 15px;
      }
      .table-value {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        background-color: white;
      }
    }
  }
  .scrollbar {
    scrollbar-gutter: stable both-edges;
    &::-webkit-scrollbar {
      width: 5px;
    }
    &::-webkit-scrollbar-track {
      background: map.get(v.$gray, "50");
      border-radius: map.get(v.$border-radius, "max");
    }
    &::-webkit-scrollbar-thumb {
      background: map.get(v.$gray, "300");
      border-radius: map.get(v.$border-radius, "max");
    }
  }
  .loading-screen {
    height: 100dvh;
  }
}
</style>
